/**
 * Copyright (C) Siemens AG 1996-2024 ALL RIGHTS RESERVED.
 */

import {environment} from '../environments/environment';
import {EXTERNAL_ROLES} from './access-request.dto';
import {
  CADMIUM_SYMBOL_URL,
  CE_MARKING_SYMBOL_URL,
  LEAD_SYMBOL_URL,
  SEPARATE_COLLECTION_SYMBOL_URL,
} from './excel-symbol-urls';

const GM = 0;
const BM = 1;
const CF = 2;
const CI = 3;
const SC = 4;
const CO = 5;
const PE = 6;

const RR = 1;

const INPUT_WAIT_TIME_MS = 600;

const SEARCH_BAR_INPUT_WAIT_TIME_MS = 400;

const MIN_SIZE_FOR_TABS = 505;
const TIME_PICKER_DATE_FORMAT = 'dd/LL/yyyy';
const TIME_PICKER_TIME_FORMAT = 'HH:mm:ss';

const BATTERY_LIST_PAGE_SIZE = 20;

const TEXT_FIELD_LENGTH = 255;
const VALUE_100 = 100;
const ALLOWED_DIGITS = 3;

const MACHINE_DOCUMENTATION_HOSTNAME = 'dpp.siemens.cloud';
const PMD_APPLICATION_TYPE = 'PMD';

const MAX_NUMBER_OF_CHILD_PASSPORTS = 100;

export function isMachineDocumentation() {
  const isMachineHostname = window.location.hostname.includes(
    MACHINE_DOCUMENTATION_HOSTNAME,
  );
  const isPmdApplication = environment.applicationType === PMD_APPLICATION_TYPE;

  return isMachineHostname || isPmdApplication;
}

export const BATTERY_PASSPORT_TITLE = 'SIEMENS Digital Product Passports';
export const MACHINE_DOCUMENTATION_TITLE = 'SIEMENS AAS Passports';
export const ERROR_PAGE_TITLE = 'Error - Page not found';

export type ExcelMappedValue =
  | string
  | Record<string, unknown>
  | Array<Record<string, unknown>>;

export interface ExcelParserConfig {
  versionString: string;
  headerLines: number;
  keysColumn: number;
  valuesColumn: number;
  valueMappings: Record<string, Record<string, ExcelMappedValue>>;
  templateAssetPath: string;
}

interface ExcelTypeConfig {
  headerLines: number;
  keysColumn: number;
  valuesColumn: number;
  valueMappings: Record<string, Record<string, ExcelMappedValue>>;
  versionPrefix: string;
  versions: string[];
}

const EXCEL_TYPE_CONFIGS: Record<string, ExcelTypeConfig> = {
  battery: {
    headerLines: 2,
    keysColumn: 3,
    valuesColumn: 5,
    versionPrefix: 'BP',
    valueMappings: {
      'generalProductInformation.batteryCategory': {
        'Stationary': 'stationary',
        'Electric Vehicle': 'ev',
        'Light Means of Transport': 'lmt',
        'Other Industrial': 'industrial',
        'Industrial (without BMS)': 'industrialWithoutBms',
      },
      'labeling.separateCollectionSymbol': {
        Yes: [{url: SEPARATE_COLLECTION_SYMBOL_URL}],
      },
      'labeling.symbolsForCadmiumAndLead': {
        'Cd': [{url: CADMIUM_SYMBOL_URL}],
        'Pb': [{url: LEAD_SYMBOL_URL}],
        'Cd+Pb': [{url: CADMIUM_SYMBOL_URL}, {url: LEAD_SYMBOL_URL}],
      },
      'labeling.ceMarking': {
        Yes: [{url: CE_MARKING_SYMBOL_URL}],
      },
    },
    versions: ['1.2.0', '1.3.0', '1.3.1', '2.0.0'],
  },
};

export function getExcelConfig(
  type: string,
  version: string,
): ExcelParserConfig | undefined {
  const typeConfig = EXCEL_TYPE_CONFIGS[type];
  if (!typeConfig?.versions.includes(version)) {
    return undefined;
  }
  return {
    headerLines: typeConfig.headerLines,
    keysColumn: typeConfig.keysColumn,
    valuesColumn: typeConfig.valuesColumn,
    valueMappings: typeConfig.valueMappings,
    versionString: `Version: ${typeConfig.versionPrefix}_${version}`,
    templateAssetPath: `assets/excel-templates/${type}/${typeConfig.versionPrefix}_${version}.xlsx`,
  };
}

export class AppSettings {
  public static ORGANIZATION_LOGO_NAME = 'organization-logo';

  public static CATEGORY_GENERAL_MANUFACTURER = GM;
  public static CATEGORY_BATTERY_MATERIAL = BM;
  public static CATEGORY_CARBON_FOOTPRINT = CF;
  public static CATEGORY_CIRCULARITY = CI;
  public static CATEGORY_SUPPLY_CHAIN = SC;
  public static CATEGORY_COMPLIANCE = CO;
  public static CATEGORY_PERFORMANCE = PE;

  public static DEFAULT_LANGUAGE = 'en';

  public static NUMBER_OF_REQUEST_RETRIES = RR;

  public static DEFAULT_TEXT_FIELD_LENGTH = TEXT_FIELD_LENGTH;
  public static DEFAULT_VALUE_100 = VALUE_100;
  public static DEFAULT_ALLOWED_DIGITS = ALLOWED_DIGITS;

  public static BATTERY_LIST_PAGE_SIZE = BATTERY_LIST_PAGE_SIZE;

  public static INPUT_TO_REQUEST_WAIT_TIME_IN_MS = INPUT_WAIT_TIME_MS;

  public static SEARCH_BAR_INPUT_TO_REQUEST_WAIT_TIME_IN_MS =
    SEARCH_BAR_INPUT_WAIT_TIME_MS;

  public static MIN_SCREEN_SIZE_FOR_TABS = MIN_SIZE_FOR_TABS;

  public static TIME_PICKER_DATE_FORMAT = TIME_PICKER_DATE_FORMAT;
  public static TIME_PICKER_TIME_FORMAT = TIME_PICKER_TIME_FORMAT;

  public static readonly EXCEL_FILE_FORMAT =
    'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet';

  public static MAX_NUMBER_OF_CHILD_PASSPORTS = MAX_NUMBER_OF_CHILD_PASSPORTS;

  public static DARK_THEME_DEFAULT = 'theme-brand-dark';
  public static LIGHT_THEME_DEFAULT = 'theme-brand-light';

  public static THEME_LOGO_DEFAULT =
    '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';
  public static THEME_LOGO_TYPE_DEFAULT = 'image/svg+xml';
  public static THEME_LOGO_WIDTH_DEFAULT = 140;
  public static THEME_LOGO_HEIGHT_DEFAULT = 50;

  public static CX_FEATURE_DEFAULT = false;
  public static SUPPLIER_API_FEATURE_DEFAULT = true;
  public static PASSPORT_HIERARCHY_FEATURE_DEFAULT = true;

  public static GRANTED_APPS_DEFAULT = ['BPP', 'UserMgmt'];
  public static ORGANIZATION_TEXT_DEFAULT =
    'No organization text configured / readable';

  public static usedRoles = [
    'SYS_ADMIN',
    'DATA_PROVIDER',
    'DATA_MANAGER',
    ...EXTERNAL_ROLES,
  ];
}
